import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from 'react-query'
import { CircularProgress, Grid, Typography } from '@mui/material'
import { formatErrorMessage } from 'utils/formatting'
import { useNotification } from 'hooks'
import { Autocomplete, Button, Input } from '@fullscreendigital/carrefour-dex-fe'
import ImageUpload from 'components/shop-reception/mobile/Common/ImageUpload/ImageUpload'
import { makeCoreGetRequest, makeCorePostRequest } from 'utils/api'
import { useFormik } from 'formik'
import { URLS } from 'components/shop-reception/mobile/constants'
import { toBoolean } from 'utils/helpers'
import { useState } from 'react'

const ExpectedDepositReturnsStep1 = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { showNotification } = useNotification()
  const truck = location.state.item
  const [uploadedImages, setUploadedImages] = useState(0)

  const { data, isLoading: isTruckLoading } = useQuery(
    ['truck'],
    async () => {
      const response = await makeCoreGetRequest(
        `/shop-reception-mobile/truck/deposit/return/truck/${truck.id}`
      )
      return response || []
    },
    {
      onError: error => {
        showNotification({
          message: `A aparut o eroare. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const confirmTruck = async (values: any) => {
    const payload = values.noticeList.map(notice => ({
      hubName: notice.hubName,
      id: notice.id,
      noticeNumber: notice.noticeNumber,
      shopCode: notice.shopCode,
      shopName: notice.shopName,
      truckId: notice.truckId
    }))

    await makeCorePostRequest(`/shop-reception-mobile/truck/deposit/return/verify-notices`, payload)
    await makeCorePostRequest(`/shop-reception-mobile/truck/deposit/return/confirm-truck`, values)
  }

  const { isLoading, mutate } = useMutation(confirmTruck, {
    onSuccess: () => {
      showNotification({
        message: `Confirmat`,
        type: 'success'
      })
      navigate(URLS.AUTH_DEPOSIT_EXPECTED_RETURNS_STEP_2, { state: { item: truck } })
    },
    onError: res => {
      showNotification({
        message: `A aparut o eroare ${formatErrorMessage(res)}`,
        type: 'error'
      })
      navigate(URLS.AUTH_DEPOSIT_EXPECTED_RETURNS_STEP_2, { state: { item: truck } })
    }
  })

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: data,
    onSubmit: async (values: any) => {
      mutate(values)
    }
  })

  if (isTruckLoading || isLoading || typeof formik.values === 'undefined') {
    return (
      <div style={{ display: 'flex' }}>
        <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
      </div>
    )
  }

  return (
    <Grid container spacing={2} justifyContent={'center'}>
      <Grid xs={10} item justifyContent="center">
        <Typography
          component="p"
          variant="medium"
          style={{ textAlign: 'center', color: 'black', marginBottom: '12px' }}
        >
          {data?.truckNumber} / {data?.driverName}
        </Typography>
        <Typography
          component="p"
          variant="medium"
          style={{ textAlign: 'center', color: 'black', marginBottom: '12px' }}
        >
          {data?.shopNamesAndCodes}
        </Typography>
      </Grid>
      {formik.values?.sealList.map((seal, index) => (
        <>
          <Grid xs={10} item>
            <Autocomplete
              variant="outlined"
              label={`Confirma sigiliu ${seal.sealCode}`}
              disableClearable
              options={[
                { label: 'Da', value: 'true' },
                { label: 'Nu', value: 'false' }
              ]}
              onChange={(event, option) =>
                formik.setFieldValue(`sealList[${index}].conform`, toBoolean(option.value))
              }
              defaultValue={
                seal.conform ? { label: 'Da', value: 'true' } : { label: 'Nu', value: 'false' }
              }
            />
          </Grid>

          <Grid xs={10} item>
            <ImageUpload
              uploadUrl={`/shop-reception-mobile/truck/deposit/return/upload/return_truck_deposit_seal/${truck?.id}`}
              onSuccess={() => {
                showNotification({
                  message: `Imaginea a fost incarcata cu succes.`,
                  type: 'success'
                })

                setUploadedImages(prev => prev + 1)
              }}
              onError={() =>
                showNotification({
                  message: `A aparut o eroare la incarcarea imaginii.`,
                  type: 'error'
                })
              }
            />
          </Grid>
        </>
      ))}

      {formik.values?.noticeList.map((notice) => (
        <>
          <Grid xs={8} item>
            <Input
              variant="outlined"
              label="Numar aviz"
              value={`Aviz ${notice.shopName || ''} ${notice.hubName || ''} ${
                notice.noticeNumber || ''
              }`}
              type="text"
              disabled
              fullWidth
            />
          </Grid>
          <Grid xs={2} item></Grid>
        </>
      ))}

      <Grid xs={10} item>
        <Button
          variant="contained"
          label="Pasul urmator"
          onClick={() => {
            formik.handleSubmit()
          }}
          style={{ width: '100%' }}
          disabled={uploadedImages < formik.values?.sealList.length || formik.isSubmitting}
        />
      </Grid>
    </Grid>
  )
}

export default ExpectedDepositReturnsStep1
